import type { StyleRenderer, RenderParams, RendererContext } from '../../types';
import { registerRenderer } from './base';
import { drawUchiwaBackground, drawUchiwaBorder, cornerRadiusPx } from '../uchiwa-shape';
import { layoutText, type CharPosition } from '../text';
import { UCHIWA_SIZES } from '../../types';

/**
 * ぷるぷる (purupuru) style — glossy bubble-like text with depth
 */
const purupuruRenderer: StyleRenderer = {
  name: 'purupuru',
  label: 'ぷるぷる',
  description: 'Glossy bubble letters with depth',

  render(ctx: CanvasRenderingContext2D, params: RenderParams, _rc: RendererContext): void {
    const { text, textColor, backgroundColor, secondaryColor, canvasWidth, canvasHeight, textOrientation } = params;

    const cr = cornerRadiusPx(
      UCHIWA_SIZES.jumbo.cornerRadius,
      UCHIWA_SIZES.jumbo.printableArea.width,
      canvasWidth,
    );

    drawUchiwaBackground(ctx, { canvasWidth, canvasHeight, cornerRadius: cr, backgroundColor });

    const pad = canvasWidth * 0.08;
    const areaW = canvasWidth - pad * 2;
    const areaH = canvasHeight - pad * 2;
    const cx = canvasWidth / 2;
    const cy = canvasHeight / 2;

    const positions = layoutText(ctx, {
      text,
      orientation: textOrientation,
      fontFamily: 'Rounded Mplus 1c',
      fontWeight: 'bold',
      areaWidth: areaW,
      areaHeight: areaH,
      centerX: cx,
      centerY: cy,
    });

    // Draw each character with bubble effect
    for (const pos of positions) {
      drawBubbleChar(ctx, pos, textColor, secondaryColor);
    }

    drawUchiwaBorder(ctx, { canvasWidth, canvasHeight, cornerRadius: cr, backgroundColor });
  },

  renderThumbnail(ctx: CanvasRenderingContext2D, size: number): void {
    ctx.fillStyle = '#FFF5F8';
    ctx.fillRect(0, 0, size, size);
    ctx.font = `bold ${size * 0.4}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.2)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetY = 2;
    ctx.fillStyle = '#FF6B9D';
    ctx.fillText('ぷ', size / 2, size / 2);
  },
};

function drawBubbleChar(
  ctx: CanvasRenderingContext2D,
  pos: CharPosition,
  textColor: string,
  secondaryColor: string,
): void {
  const { char, x, y, fontSize, rotate } = pos;
  const font = `bold ${fontSize}px 'Rounded Mplus 1c', 'Noto Sans JP', sans-serif`;

  ctx.save();
  ctx.translate(x, y);
  if (rotate) ctx.rotate(rotate);

  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Layer 1: Drop shadow
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.20)';
  ctx.shadowBlur = fontSize * 0.12;
  ctx.shadowOffsetX = fontSize * 0.03;
  ctx.shadowOffsetY = fontSize * 0.06;
  ctx.fillStyle = textColor;
  ctx.fillText(char, 0, 0);
  ctx.restore();

  // Layer 2: Outer glow / secondary outline
  ctx.strokeStyle = secondaryColor;
  ctx.lineWidth = fontSize * 0.06;
  ctx.lineJoin = 'round';
  ctx.strokeText(char, 0, 0);

  // Layer 3: Main fill
  ctx.fillStyle = textColor;
  ctx.fillText(char, 0, 0);

  // Layer 4: Gloss highlight (upper portion)
  ctx.save();
  ctx.globalAlpha = 0.30;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillText(char, 0, -fontSize * 0.06);
  ctx.restore();

  // Layer 5: Small bright highlight dot
  ctx.save();
  ctx.globalAlpha = 0.5;
  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  ctx.arc(-fontSize * 0.15, -fontSize * 0.18, fontSize * 0.05, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.restore();
}

registerRenderer(purupuruRenderer);
export default purupuruRenderer;
